/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Zap, UserCheck, FileSignature, RefreshCw, ExternalLink, MessageSquareText, MapPin, Link2 } from "lucide-react";
import { toast } from "../../../../components/ui/sonner";
import { useApp } from "../../../../context/AppContext";
import { authStore } from "../../../../lib/api";
import { T } from "../../../../lib/i18n";
import { fmtDT } from "../../../../lib/usDate";
import { ZoningForm } from "./ZoningForm";
import { SiteMeasurePanel } from "./SiteMeasurePanel";
import { SchematicPanel, RevisionRow } from "./SchematicPanel";
import { SignoffPanel } from "./SignoffPanel";

export const PRE_KEYS = ["pre_consult", "contract", "site_measure", "schematic", "owner_signoff"];
export const fileUrl = (fid) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;
export const dt = (v) => (v ? fmtDT(v) : "—");

export const usePre = (pid) => {
  const { api } = useApp();
  const [pre, setPre] = useState(null);
  const reload = useCallback(() => api.get(`/design/v3/projects/${pid}/pre`).then(({ data }) => setPre(data)).catch((e) => setPre({ error: e?.detailText || e.message })), [api, pid]);
  useEffect(() => { reload(); }, [reload]);
  return { pre, reload };
};

export const Auto = ({ children }) => <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-violet-700 bg-violet-100 rounded-full px-1.5 py-0.5" data-testid="dv3-pre-auto-badge"><Zap className="h-3 w-3" />{children || T("系统自动")}</span>;

export const Who = ({ d, label, tid }) => (
  <span className="inline-flex items-center gap-1 text-[11px] text-slate-600" data-testid={tid}>
    <UserCheck className="h-3 w-3 text-slate-400" />{T(label)}：<b className={d ? "text-slate-800" : "text-amber-600"}>{d ? (d.name || d.username) : T("未指派")}</b>
  </span>
);

export const Pill = ({ ok, warn, children, tid }) => <span data-testid={tid} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${ok ? "bg-emerald-100 text-emerald-700" : warn ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{children}</span>;

export const Card = ({ title, icon: I, right, children, tid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-1.5" data-testid={tid}>
    <div className="flex-wrap md:flex-nowrap flex items-center gap-1.5 text-[11px] font-bold text-slate-700">{I && <I className="h-3.5 w-3.5 text-violet-500" />}{title}<span className="ml-auto flex items-center gap-1">{right}</span></div>
    {children}
  </div>
);

export const PreStagePanel = ({ stageKey, pid, meta, onChanged }) => {
  const { api } = useApp();
  const { pre, reload } = usePre(pid);
  if (!pre) return <div className="py-3 text-center text-slate-400" data-testid={`dv3-pre-${stageKey}-loading`}><Loader2 className="h-4 w-4 animate-spin inline" /></div>;
  if (pre.error) return <p className="text-[11px] text-red-600" data-testid={`dv3-pre-${stageKey}-error`}>{String(pre.error)}</p>;
  const done = () => { reload(); onChanged?.(); };
  const props = { pre, api, pid, meta, reload: done };
  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-2.5 space-y-2" data-testid={`dv3-pre-${stageKey}`}>
      {stageKey === "pre_consult" && <PreConsultPanel {...props} />}
      {stageKey === "contract" && <ContractPanel {...props} />}
      {stageKey === "site_measure" && <SiteMeasurePanel {...props} />}
      {stageKey === "schematic" && <SchematicPanel {...props} />}
      {stageKey === "owner_signoff" && <SignoffPanel {...props} />}
    </div>
  );
};

const PreConsultPanel = ({ pre, api, pid, reload }) => {
  const [busy, setBusy] = useState(false);
  const reassign = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/design/v3/projects/${pid}/pre/auto-assign`, { overwrite: true }); toast.success(T("已按项目分工重新指派 {0} 项", [data.assigned])); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Auto>{T("自动分工")}</Auto>
        <Who d={pre.designers.mk} label="统一负责人 · 前期营销方案" tid="dv3-pre-who-mk" />
        <Who d={pre.designers.cn} label="初步 Site/Floor 方案 · 中国建筑设计师" tid="dv3-pre-who-cn" />
        <Who d={pre.designers.us} label="政策核实 · 美国建筑设计师" tid="dv3-pre-who-us" />
        {pre.isManager && <button type="button" data-testid="dv3-pre-reassign" disabled={busy} onClick={reassign} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto text-[10px] text-violet-700 font-semibold inline-flex items-center gap-1 hover:underline"><RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />{T("按项目设置重新分工")}</button>}
      </div>
      <ZoningForm pre={pre} api={api} pid={pid} reload={reload} />
      <OwnerFeedbackCard pre={pre} api={api} pid={pid} reload={reload} stageKey="pre_consult" />
    </>
  );
};

/* 业主通过销售分享链接提出的图上批注 / 修改意见（本阶段文件），设计师在此回复 */
export const OwnerFeedbackCard = ({ pre, api, pid, reload, stageKey }) => {
  const aids = new Set((pre.tasks || []).filter((t) => t.stageKey === stageKey).flatMap((t) => (t.files || []).map((f) => f.aid)));
  const fileOf = (aid) => (pre.tasks || []).flatMap((t) => t.files || []).find((f) => f.aid === aid);
  const revs = (pre.revisions || []).filter((r) => (r.fileAid && aids.has(r.fileAid)) || r.stageKey === stageKey);
  const marks = (pre.markups || []).filter((m) => aids.has(m.fileAid));
  const link = (pre.shareLinks || []).find((l) => l.stageKey === stageKey);
  const open = revs.filter((r) => r.status === "open").length;
  return (
    <Card title={T("业主反馈 · 图上批注 / 修改意见")} icon={MessageSquareText} tid={`dv3-pre-owner-fb-${stageKey}`} right={<>{open > 0 && <Pill warn tid={`dv3-pre-owner-fb-open-${stageKey}`}>{T("待回复 {0}", [open])}</Pill>}<span className="text-[10px] text-slate-400">{T("批注 {0} · 修改意见 {1}", [marks.length, revs.length])}</span></>}>
      {link ? <p className="text-[10px] text-slate-500 flex flex-wrap items-center gap-1" data-testid={`dv3-pre-share-link-${stageKey}`}><Link2 className="h-3 w-3 text-indigo-500" />{T("销售已分享业主链接（仅本阶段已发布文件）")}：<a href={link.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline truncate max-w-[240px]">{link.url}</a><span>· {T("含 {0} 份 · 业主打开 {1} 次", [link.visibleCount, link.views])}</span></p>
        : <p className="text-[10px] text-slate-400" data-testid={`dv3-pre-share-none-${stageKey}`}>{T("销售尚未生成业主分享链接；上传的文件默认仅内部可见，由销售在线索页勾选发布。")}</p>}
      {marks.length > 0 && <ul className="space-y-0.5 text-[11px]" data-testid={`dv3-pre-owner-marks-${stageKey}`}>{marks.map((m) => <li key={m.id} data-testid={`dv3-pre-owner-mark-${m.id}`}><MapPin className="h-3 w-3 inline text-red-500" /> {dt(m.at)} · {m.by && m.by !== "owner" ? `${m.by} · ` : ""}{fileOf(m.fileAid)?.type || m.fileAid}{(m.page || 1) > 1 ? ` · P${m.page}` : ""}：{m.text}</li>)}</ul>}
      {revs.length > 0 && <div className="space-y-1.5 max-h-72 overflow-y-auto">{revs.map((r) => <RevisionRow key={r.id} r={r} pre={pre} api={api} pid={pid} reload={reload} />)}</div>}
      {!marks.length && !revs.length && <p className="text-[11px] text-slate-400">{T("业主尚未提出批注或修改意见。")}</p>}
    </Card>
  );
};

const SRC = { crm: "CRM 合同", qb_import: "QuickBooks 导入", finance_manual: "财务手工登记", design_payment: "设计费里程碑" };

/* 合同签署阶段：数据全部由「CRM 合同 → 财务首款」只读同步进来，设计部不手动操作。
   销售信息只在这里显示姓名（联系方式集中在项目表头「销售 xxx」旁，见 ProjectDrawer 的 dv3-head-team）——
   销售是项目级属性、不随阶段变化，放在阶段面板里一旦阶段关闭就看不到了。 */
const ContractPanel = ({ pre }) => {
  const c = pre.contract || {};
  return (
    <>
      <p className="text-[11px] text-slate-600 flex flex-wrap items-center gap-1.5"><Auto>{T("只读同步")}</Auto>{T("合同由项目销售在 CRM 完成签署；财务确认首款到账后本阶段自动完成并触发现场测量，设计部无需手动操作。")}</p>
      <div className="grid gap-2">
        <Card title={T("合同签署")} icon={FileSignature} tid="dv3-pre-contract-card" right={<Pill ok={c.status === "signed"} warn={c.status !== "signed"} tid="dv3-pre-contract-status">{c.status === "signed" ? T("已签署") : T("待销售签约")}</Pill>}>
          {c.status === "signed" ? (
            <div className="text-[11px] text-slate-600 space-y-0.5">
              <p>{T("签署时间")} <b className="text-slate-800">{dt(c.signedAt)}</b>{c.signedOnline ? <span className="text-emerald-700"> · {T("业主在线签署")}</span> : ""}</p>
              <p>{T("合同额")} <b className="text-slate-800">${Number(c.amount || 0).toLocaleString()}</b> · {T("销售")} {c.salesName || "—"} · {T("来源")} {SRC[c.source] || c.source}</p>
              <p className="text-slate-400">{T("同步于")} {dt(c.syncedAt)}</p>
            </div>
          ) : <p className="text-[11px] text-slate-500">{T("等待 CRM 合同签署（销售发送 → 业主签字 / 手工签约）后自动填入。")}{pre.lead && <a href={`/crm/leads/${pre.lead.id}`} className="ml-1 text-violet-700 inline-flex items-center gap-0.5 hover:underline" data-testid="dv3-pre-contract-lead">{T("查看线索")}<ExternalLink className="h-3 w-3" /></a>}</p>}
        </Card>
      </div>
    </>
  );
};
